import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, Shield, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [logoutOthers, setLogoutOthers] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/auth/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `parsel-ekspertiz-verilerim-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Verileriniz indirildi", {
        description: `${data.expertise_count} ekspertiz kaydı dahil.`,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Hesabınız ve tüm verileriniz silindi");
      await logout();
      navigate("/giris", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Yeni şifre en az 8 karakter olmalı");
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("Yeni şifre mevcut şifreden farklı olmalı");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        logout_others: logoutOthers,
      });
      toast.success(
        logoutOthers
          ? "Şifreniz güncellendi. Diğer cihazlardaki oturumlar sonlandırıldı."
          : "Şifreniz güncellendi",
      );
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setLogoutOthers(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="border-b border-zinc-200 px-6 py-4 sticky top-0 bg-white/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
            data-testid="settings-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Ana Sayfa
          </Link>
          <h1 className="text-lg font-bold tracking-tight">Ayarlar</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {/* Account */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
            Hesap
          </h2>
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="text-xs text-zinc-500">E-posta</div>
            <div className="text-sm font-medium mt-0.5" data-testid="settings-email">
              {user?.email}
            </div>
            <div className="text-xs text-zinc-500 mt-3">Ad Soyad</div>
            <div className="text-sm font-medium mt-0.5">{user?.name}</div>
          </div>
        </section>

        {/* Rehber Turu */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
            Rehber Turu
          </h2>
          <div className="border border-zinc-200 rounded-sm p-4 flex items-center justify-between gap-3">
            <div className="text-sm text-zinc-600 leading-relaxed">
              Uygulamayı ilk kez kullanıyor gibi kısa bir rehber turu görmek ister misiniz?
              Ekspertiz eklemek, harita üzerinde sağ tıklamak, arama yapmak gibi temel özellikler gösterilir.
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                localStorage.removeItem("kolayarsivim_tour_seen");
                navigate("/", { replace: true });
              }}
              className="rounded-sm shrink-0"
              data-testid="restart-tour-btn"
            >
              Turu Başlat
            </Button>
          </div>
        </section>

        {/* Password change */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
            Şifre Değiştir
          </h2>
          <form
            onSubmit={handleChangePassword}
            className="border border-zinc-200 rounded-sm p-4 space-y-4"
            data-testid="change-password-form"
          >
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-xs">
                Mevcut Şifre
              </Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrent ? "text" : "password"}
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid="current-password-input"
                  placeholder="Mevcut şifreniz"
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  tabIndex={-1}
                  aria-label={showCurrent ? "Şifreyi gizle" : "Şifreyi göster"}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-xs">
                Yeni Şifre
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNew ? "text" : "password"}
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                  placeholder="En az 8 karakter"
                  autoComplete="new-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  tabIndex={-1}
                  aria-label={showNew ? "Şifreyi gizle" : "Şifreyi göster"}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password-confirm" className="text-xs">
                Yeni Şifre (Tekrar)
              </Label>
              <Input
                id="new-password-confirm"
                type={showNew ? "text" : "password"}
                required
                minLength={8}
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                data-testid="new-password-confirm-input"
                placeholder="Yeni şifreyi tekrar girin"
                autoComplete="new-password"
              />
              {newPasswordConfirm && newPassword !== newPasswordConfirm && (
                <p className="text-[11px] text-red-600" data-testid="password-mismatch-msg">
                  Şifreler eşleşmiyor.
                </p>
              )}
            </div>

            <div className="pt-1 flex items-center justify-between gap-3 flex-wrap">
              <label
                htmlFor="logout-others"
                className="flex items-start gap-2 cursor-pointer max-w-[60%]"
              >
                <Checkbox
                  id="logout-others"
                  checked={logoutOthers}
                  onCheckedChange={(v) => setLogoutOthers(!!v)}
                  data-testid="logout-others-checkbox"
                  className="mt-0.5"
                />
                <span className="text-[12px] text-zinc-600 leading-snug">
                  Diğer tüm cihazlardaki oturumları kapat
                  <span className="block text-[11px] text-zinc-400">
                    Bu cihazdaki oturum açık kalır.
                  </span>
                </span>
              </label>
              <Button
                type="submit"
                disabled={changingPw}
                className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
                data-testid="change-password-submit-btn"
              >
                {changingPw ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Şifreyi Güncelle
                  </>
                )}
              </Button>
            </div>
          </form>
        </section>

        {/* KVKK: Data export */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
            KVKK — Kişisel Verilerim
          </h2>
          <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-zinc-700" />
                  <div className="font-semibold text-sm">Verilerimi İndir</div>
                </div>
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                  KVKK Md. 11 kapsamında hesabınız ve tüm ekspertiz kayıtlarınızı
                  JSON formatında indirin.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
                className="rounded-sm shrink-0"
                data-testid="export-data-btn"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    İndir
                  </>
                )}
              </Button>
            </div>

            <div className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-red-600" />
                  <div className="font-semibold text-sm text-red-700">
                    Hesabımı Sil
                  </div>
                </div>
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                  Hesabınız ve tüm ekspertiz kayıtlarınız kalıcı olarak silinir.
                  Bu işlem <strong>geri alınamaz</strong>.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDelete(true)}
                className="rounded-sm shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Sil
              </Button>
            </div>
          </div>
        </section>

        {/* Legal */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
            Yasal
          </h2>
          <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100 text-sm">
            <Link to="/aydinlatma" className="block p-4 hover:bg-zinc-50 flex items-center gap-2">
              <Shield className="h-4 w-4 text-zinc-500" /> Aydınlatma Metni
            </Link>
            <Link to="/gizlilik" className="block p-4 hover:bg-zinc-50 flex items-center gap-2">
              <Shield className="h-4 w-4 text-zinc-500" /> Gizlilik Politikası
            </Link>
            <Link to="/kullanim" className="block p-4 hover:bg-zinc-50 flex items-center gap-2">
              <Shield className="h-4 w-4 text-zinc-500" /> Kullanım Şartları
            </Link>
            <Link to="/cerez" className="block p-4 hover:bg-zinc-50 flex items-center gap-2">
              <Shield className="h-4 w-4 text-zinc-500" /> Çerez Politikası
            </Link>
          </div>
        </section>
      </main>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hesabımı ve tüm verilerimi sil</AlertDialogTitle>
            <AlertDialogDescription>
              Hesabınız ve tüm ekspertiz kayıtlarınız kalıcı olarak silinecek.
              Bu işlem <strong>geri alınamaz</strong>. Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-account-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Evet, sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
